import { useEffect, useState } from 'react';
import { useStore } from '../state/store';
import { Button, Card, Chip, EmptyState, Icon, SectionHeader, Toggle } from '../ui';
import { EVENT_META, eventHeadline, blankEvent, EVENT_KINDS_BY_GROUP } from '../events/meta';
import type { EventItem, EventKind } from '../types';
import { EventEditor } from './events/EventEditor';

interface Props {
  onGoTo: (tab: string) => void;
  /** When set, the panel expands the matching event inline. Reset after
   *  the user sees it (pass `onFocusEventHandled` to clear the ID). */
  focusEventId?: string | null;
  onFocusEventHandled?: () => void;
}

/**
 * Dashboard-side control panel for the active scenario. This is THE edit
 * surface that sits next to the chart — toggles, quick-adds, and full
 * inline event editing all happen here so the user can author what-ifs
 * while watching the projection update live. Every mutation autosaves
 * and the chart re-renders within the store's reload loop.
 */
export function ScenarioPanel({ onGoTo, focusEventId, onFocusEventHandled }: Props) {
  const {
    activeScenario,
    compareScenarios,
    saveScenario,
    saveEventInActive,
    deleteEventInActive,
  } = useStore();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  // Open a specific event inline when asked (e.g., from a loan-strip click).
  useEffect(() => {
    if (focusEventId && activeScenario.events.some(e => e.id === focusEventId)) {
      setExpandedId(focusEventId);
      onFocusEventHandled?.();
    }
  }, [focusEventId, activeScenario.events, onFocusEventHandled]);

  async function toggleEvent(e: EventItem) {
    await saveEventInActive({ ...e, enabled: !e.enabled } as EventItem);
  }

  async function quickAdd(kind: EventKind) {
    const e = blankEvent(kind, 1);
    await saveScenario({
      ...activeScenario,
      events: [...activeScenario.events, e],
    });
    setAdding(false);
    // Open the newly-created event inline so the user can start editing
    // right there without leaving the chart.
    setExpandedId(e.id);
  }

  return (
    <Card flush>
      <SectionHeader
        bordered
        muted
        title={
          <span className="inline-flex items-center gap-2">
            <span
              className="flex h-5 w-5 items-center justify-center rounded-md text-xs"
              style={{ backgroundColor: `${activeScenario.color}22`, color: activeScenario.color }}
              aria-hidden
            >
              {activeScenario.icon}
            </span>
            <span className="normal-case">{activeScenario.name}</span>
          </span>
        }
        subtitle={
          compareScenarios.length > 0
            ? `vs ${compareScenarios.map(c => c.name).join(', ')}`
            : 'Click an event to edit — the chart updates as you type'
        }
        right={
          <button
            type="button"
            onClick={() => onGoTo('scenarios')}
            className="text-[11px] font-semibold text-accent-600 dark:text-accent-500"
          >
            All scenarios →
          </button>
        }
      />

      {activeScenario.events.length === 0 ? (
        <div className="p-4">
          <EmptyState
            icon="🗻"
            title="No events yet"
            description="Add a purchase, income change, or life event to explore what-ifs. The chart above will reflect each change."
            action={
              <Button
                size="sm"
                leading={<Icon name="plus" size={12} />}
                onClick={() => setAdding(true)}
              >
                Add event
              </Button>
            }
          />
        </div>
      ) : (
        <ul>
          {[...activeScenario.events]
            .sort((a, b) => a.yearOffset - b.yearOffset)
            .map(e => {
              const meta = EVENT_META[e.kind];
              const isExpanded = expandedId === e.id;
              return (
                <li
                  key={e.id}
                  className="border-b border-ink-100 last:border-b-0 dark:border-ink-700"
                >
                  <div className="flex items-center gap-3 px-4 py-3">
                    <Toggle
                      checked={e.enabled}
                      onChange={() => toggleEvent(e)}
                      ariaLabel={`Toggle ${('name' in e && e.name) || meta.label}`}
                      size="sm"
                    />
                    <button
                      type="button"
                      onClick={() => setExpandedId(isExpanded ? null : e.id)}
                      className={`flex min-w-0 flex-1 items-center gap-2 rounded-md p-1 -m-1 text-left transition hover:bg-ink-50 dark:hover:bg-ink-700/60 ${
                        !e.enabled ? 'opacity-50' : ''
                      }`}
                      aria-expanded={isExpanded}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline gap-2">
                          <span
                            className={`truncate text-sm font-semibold ${!e.enabled ? 'line-through' : ''}`}
                          >
                            {('name' in e && e.name) || meta.label}
                          </span>
                          <span className="shrink-0 font-mono text-[11px] font-semibold tabular-nums text-ink-500 dark:text-ink-300">
                            {eventHeadline(e)}
                          </span>
                        </div>
                        <div className="truncate text-[11px] text-ink-500 dark:text-ink-300">
                          <Chip
                            tone={meta.tone as 'neg' | 'pos' | 'accent' | 'warning' | 'neutral' | 'muted'}
                            size="sm"
                          >
                            {meta.label}
                          </Chip>
                          {' · '} year +{e.yearOffset}
                        </div>
                      </div>
                      <Icon
                        name={isExpanded ? 'chevron-down' : 'chevron-right'}
                        size={14}
                        className="shrink-0 text-ink-400"
                      />
                    </button>
                  </div>
                  {isExpanded && (
                    <div className="border-t border-ink-100 bg-ink-50/40 px-3 py-3 dark:border-ink-700 dark:bg-ink-800/40">
                      <EventEditor
                        event={e}
                        onChange={next => saveEventInActive(next)}
                        onDelete={async () => {
                          await deleteEventInActive(e.id);
                          setExpandedId(null);
                        }}
                        onClose={() => setExpandedId(null)}
                      />
                    </div>
                  )}
                </li>
              );
            })}
        </ul>
      )}

      {adding ? (
        <div className="space-y-2 border-t border-ink-100 p-3 dark:border-ink-700">
          <div className="flex items-baseline justify-between">
            <span className="text-xs font-semibold uppercase tracking-wide text-ink-500 dark:text-ink-300">
              Pick an event type
            </span>
            <button
              type="button"
              onClick={() => setAdding(false)}
              className="text-[11px] font-semibold text-ink-500 hover:text-ink-700 dark:text-ink-300"
            >
              Cancel
            </button>
          </div>
          {EVENT_KINDS_BY_GROUP.map(g => (
            <div key={g.group}>
              <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-ink-500 dark:text-ink-300">
                {g.label}
              </div>
              <div className="grid grid-cols-2 gap-1.5">
                {g.kinds.map(k => {
                  const meta = EVENT_META[k];
                  return (
                    <button
                      key={k}
                      type="button"
                      onClick={() => quickAdd(k)}
                      className="flex items-center gap-2 rounded-lg border border-ink-100 bg-white p-2 text-left text-xs transition hover:border-accent-500 dark:border-ink-700 dark:bg-ink-800"
                    >
                      <Icon name={meta.icon} size={14} />
                      <span className="truncate font-semibold">{meta.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="flex w-full items-center justify-center gap-2 border-t border-ink-100 px-4 py-3 text-sm font-semibold text-accent-600 transition hover:bg-ink-50 dark:border-ink-700 dark:text-accent-500 dark:hover:bg-ink-800"
        >
          <Icon name="plus" size={14} /> Add event
        </button>
      )}
    </Card>
  );
}
